import { useEffect, useRef, useState } from 'react';

// Google Identity Services "Continue with Google". Hidden when VITE_GOOGLE_WEB_CLIENT_ID is unset
// (dev without keys), per PHASE2-CONTRACT "missing third-party keys degrade gracefully".

interface GsiId {
  initialize(cfg: { client_id: string; callback: (r: { credential?: string }) => void; ux_mode?: 'popup'; auto_select?: boolean; cancel_on_tap_outside?: boolean }): void;
  renderButton(el: HTMLElement, opts: Record<string, string | number>): void;
}
declare global {
  interface Window {
    google?: { accounts: { id: GsiId } };
  }
}

// hl=en: the button label follows the page language, not the browser's (BO-21).
const SRC = 'https://accounts.google.com/gsi/client?hl=en';
let loader: Promise<void> | null = null;

function loadGsi(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (loader) return loader;
  loader = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      loader = null;
      reject(new Error('gsi'));
    };
    document.head.appendChild(s);
  });
  return loader;
}

export const googleClientId = (import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID || '').trim();

export function GoogleButton({ onIdToken, dark }: { onIdToken(idToken: string): void; dark: boolean }) {
  const host = useRef<HTMLDivElement>(null);
  const cb = useRef(onIdToken);
  cb.current = onIdToken;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!googleClientId) return;
    let live = true;
    loadGsi()
      .then(() => {
        const id = window.google?.accounts.id;
        if (!live || !id || !host.current) return;
        id.initialize({
          client_id: googleClientId,
          ux_mode: 'popup',
          callback: (r) => {
            if (r.credential) cb.current(r.credential);
          },
        });
        host.current.replaceChildren();
        id.renderButton(host.current, {
          type: 'standard',
          theme: dark ? 'filled_black' : 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          logo_alignment: 'left',
          width: 320,
          locale: 'en',
        });
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [dark]);

  if (!googleClientId) return null;
  if (failed) return <p className="t-caption muted">Google sign-in didn't load. Use your email instead.</p>;
  return <div className="gsi" ref={host} />;
}
